import { FetcherError } from '@bino/utils/fetcher';
import { createContext, ReactNode, use, useState } from 'react';
import useSWR, { KeyedMutator } from 'swr';
import { BiaxCartResponse } from '../types';

type CartContextType = {
    productSearchUrl?: string;
    isFallbackPage: boolean;
    isAuthorized: boolean;
    iconColor: 'primary' | 'white';
    cart: BiaxCartResponse;
    loadingCart: boolean;
    refreshCart: KeyedMutator<BiaxCartResponse>;
    error: unknown;
    programmeId?: number | null;
    globalCartError: FetcherError | null;
    setGlobalCartError: (error: FetcherError | null) => void;
    disableCart: boolean;
    emergencyContactFormUrl?: string;
};

const CartContext = createContext<CartContextType | undefined>(undefined);

export type ShoppingCartProviderProps = {
    productSearchUrl?: string;
    isFallbackPage: boolean;
    isAuthorized: boolean;
    iconColor: 'primary' | 'white';
    children: ReactNode;
    programmeId?: number | null;
    disableCart: boolean;
    emergencyContactFormUrl?: string;
};
const isAuthError = (error: FetcherError | null) => error?.status === 401 || error?.status === 403;
const emptyCart: BiaxCartResponse = { items: [], biaxCartDetailPageUrl: '' };
export const ShoppingCartProvider = ({
    productSearchUrl,
    isFallbackPage,
    isAuthorized,
    iconColor,
    children,
    programmeId,
    disableCart,
    emergencyContactFormUrl,
}: ShoppingCartProviderProps) => {
    const [globalCartError, setGlobalCartError] = useState<FetcherError | null>(null);
    const {
        data: cart = emptyCart,
        isLoading,
        isValidating,
        mutate,
        error,
    } = useSWR<BiaxCartResponse, FetcherError>(
        !disableCart && isAuthorized && !isAuthError(globalCartError) ? '/api/cart' : null,
        {
            shouldRetryOnError: false,
            keepPreviousData: true,
            focusThrottleInterval: 60_000,
            onError: (error) => {
                if (isAuthError(error)) {
                    setGlobalCartError(error);
                }
            },
        },
    );

    return (
        <CartContext
            value={{
                productSearchUrl,
                isFallbackPage,
                isAuthorized,
                iconColor,
                cart,
                refreshCart: mutate,
                loadingCart: isLoading || isValidating,
                error,
                programmeId,
                globalCartError,
                setGlobalCartError,
                disableCart,
                emergencyContactFormUrl,
            }}
        >
            {children}
        </CartContext>
    );
};

export const useCartContext = () => {
    const context = use(CartContext);
    if (context === undefined) {
        throw new Error('useCartContext must be used within a ShoppingCartProvider');
    }
    return context;
};
